import { SectionCard, DataTable, type Column } from '@datahub/design-system'
import { ExternalLink } from '../../components/ExternalLink'
import { plainText } from '../../lib/prose'

/**
 * A portfolio company as the fund snapshot now holds it. Snapshots pulled before this
 * card existed hold a bare name, so both shapes have to read.
 */
export interface CorePortfolioCompany {
  name: string
  slug: string | null
  status: string | null
  foundingYear: number | null
  websiteUrl: string | null
  companyStage: string | null
  ownershipStatus: string | null
  employeeCount: string | null
  address: string | null
  /** A paragraph; the column clips it and the hover carries the whole thing. */
  description: string | null
}

export type StoredPortfolioCompany = CorePortfolioCompany | string

/** Widens an older snapshot's bare names into the record the table expects. */
const BLANK = {
  slug: null,
  status: null,
  foundingYear: null,
  websiteUrl: null,
  companyStage: null,
  ownershipStatus: null,
  employeeCount: null,
  address: null,
  description: null,
}

/**
 * Widens an older snapshot into the record the table expects. A snapshot taken before
 * this card existed holds a bare name; one taken between then and now holds the first
 * four columns. Both read, with the columns they predate left empty — the alternative is
 * a card that renders nothing until someone re-pulls every fund.
 */
export function normalisePortfolio(entries: StoredPortfolioCompany[] | null | undefined): CorePortfolioCompany[] {
  return (entries ?? []).map((entry) =>
    typeof entry === 'string' ? { ...BLANK, name: entry } : { ...BLANK, ...entry },
  )
}

/**
 * The companies Stears records a fund as having backed. This was one comma-joined line
 * in the card below, which for a fund with sixteen of them read as a paragraph; a table
 * gives each company its own row and room for what Core knows about it.
 *
 * Sector and country are not here on purpose. Core will not return either through this
 * nesting — the request hangs rather than answering — so an address is the only geography
 * on offer. Every scalar Core does hold arrives together in about 400ms, so the columns
 * are the ones actually filled in: see `PORTFOLIO_FIELDS` in the Core client for the
 * measured rates and for why type, legal status and key products are left out.
 */
export function StearsPortfolioCard({ companies }: { companies: CorePortfolioCompany[] }) {
  if (companies.length === 0) return null

  const columns: Column<CorePortfolioCompany>[] = [
    { key: 'name', header: 'Company', width: 260, sticky: true, render: (c) => c.name },
    {
      key: 'description',
      header: 'Description',
      width: 360,
      render: (c) => {
        const description = plainText(c.description)
        return description ? <span title={description}>{description}</span> : '-'
      },
    },
    { key: 'status', header: 'Status', width: 130, render: (c) => c.status ?? '-' },
    { key: 'stage', header: 'Stage', width: 110, render: (c) => c.companyStage ?? '-' },
    { key: 'ownership', header: 'Ownership', width: 110, render: (c) => c.ownershipStatus ?? '-' },
    { key: 'employees', header: 'Employees', width: 110, render: (c) => c.employeeCount ?? '-' },
    { key: 'founded', header: 'Founded', width: 90, render: (c) => (c.foundingYear ? String(c.foundingYear) : '-') },
    {
      key: 'address',
      header: 'Head office',
      width: 260,
      render: (c) => (c.address ? <span title={c.address}>{c.address}</span> : '-'),
    },
    {
      key: 'website',
      header: 'Website',
      width: 220,
      render: (c) => (c.websiteUrl ? <ExternalLink value={c.websiteUrl} maxLength={28} /> : '-'),
    },
  ]

  return (
    <SectionCard title={`Portfolio companies Stears lists (${companies.length})`}>
      <DataTable
        columns={columns}
        rows={companies}
        rowKey={(c) => c.slug ?? c.name}
        caption="Portfolio companies on Stears"
        maxHeight="420px"
      />
    </SectionCard>
  )
}
